/*
  PULSE colour showcase (sections/pulse-colour-showcase.liquid): the selected colour's tester stands in front, the
  other three behind it. Picking a colour here (a tester in the picture or the colour list) sets the colour input of
  the main product form and fires a bubbling change event, so Dawn's own code switches the variant (URL, price,
  gallery, buttons). A colour picked in the main picker reaches the showcase through Dawn's variantChange event.
  The showcase never fires a change from that event, so there is no loop.
*/
if (!customElements.get('pulse-colour-showcase')) {
  class PulseColourShowcase extends HTMLElement {
    /**
     * After the picture swaps, the testers in it stand for other colours: the second click of a double-click (or
     * double-tap) on a tester would land on the colour that moved into that spot. So clicks on the testers in the
     * picture are ignored for this long after a swap (covers the 300 ms crossfade, a double-tap and a default-speed
     * double-click), and so are the later clicks of any double-click (event.detail > 1, for slower double-click
     * settings). The colour list is not affected: its buttons never move.
     */
    static SETTLE_MS = 500;

    /**
     * Slot 0 (front) = the selected colour, slots 1-3 = the other colours in showcase order. The section's Liquid
     * renders the same order for first paint; tests/showcase.test.mjs checks both against the render sidecars.
     */
    static slotOrder(selected, colours) {
      return [selected, ...colours.filter((colour) => colour !== selected)];
    }

    /**
     * The main picker's control for one value of the option: the radio input (button and swatch pickers) or the
     * select that holds the matching option (dropdown pickers). Looked up fresh every time: Dawn re-renders the
     * variant picker on each variant change.
     */
    static pickerControl(root, optionName, value) {
      for (const element of root?.querySelectorAll('variant-selects [data-option-name]') ?? []) {
        if (element.dataset.optionName !== optionName || element.value !== value) continue;
        if (element.tagName === 'INPUT') return element;
        if (element.tagName === 'OPTION') return element.closest('select');
      }
      return null;
    }

    /** Selects the value and fires the bubbling change event Dawn's variant-selects listens to; false = already set. */
    static choose(control, value) {
      if (control.tagName === 'SELECT') {
        if (control.value === value) return false;
        control.value = value;
      } else {
        if (control.checked) return false;
        control.checked = true;
      }
      control.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    connectedCallback() {
      this.colours = (this.dataset.colours || '').split(',');
      this.selected = this.dataset.selected;
      this.held = null;
      this.settledAt = 0;
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
      this.unsubscribe =
        typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
          ? subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this))
          : null;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.unsubscribe?.();
    }

    get productInfo() {
      return document.querySelector(`product-info[id^="MainProduct-"][data-product-id="${this.dataset.productId}"]`);
    }

    get listButtons() {
      return Array.from(this.querySelectorAll('.pulse-showcase__colour'));
    }

    onClick(event) {
      const button = event.target.closest('button[data-colour]');
      if (!button || button.hidden) return;
      // Focus never stays on a tester in the picture: its button is aria-hidden and stands for another colour after
      // the next swap. After a tap there, focus goes to the colour list button of the colour now in front.
      const inPicture = button.classList.contains('pulse-showcase__slot');
      // A tester in the picture: ignore the later clicks of a double-click and any click while the picture settles.
      if (inPicture && (event.detail > 1 || performance.now() < this.settledAt)) {
        this.listButton(this.selected)?.focus({ preventScroll: true });
        return;
      }
      const colour = button.dataset.colour;
      const item = this.listButton(colour);
      const value = item?.dataset.value;
      if (!value) return;
      if (inPicture) item.focus({ preventScroll: true });

      // The colour last requested is the one the main picker names, not this.selected: Dawn's own picker changes at
      // once, the picture only when Dawn has loaded that variant (variantChange). So a click on the colour Dawn is
      // switching away from sets the picker back, and a click on the colour Dawn is still loading is not lost either.
      const control = PulseColourShowcase.pickerControl(this.productInfo, this.dataset.optionName, value);
      const requested = Boolean(control) && PulseColourShowcase.choose(control, value); // false: the picker names it already
      if (!requested && colour === this.selected) return;
      // With a picker, Dawn's variantChange for this colour is on its way (requested now, or already loading): keep the
      // page in place when it arrives.
      if (control) {
        this.held = { colour, focus: inPicture ? item : document.activeElement, top: this.getBoundingClientRect().top };
      }
      this.show(colour);
    }

    listButton(colour) {
      return this.listButtons.find((item) => item.dataset.colour === colour);
    }

    onVariantChange({ data } = {}) {
      const info = this.productInfo;
      if (!data?.variant || !info || data.sectionId !== (info.dataset.originalSection || info.dataset.section)) return;
      const value = data.variant.options?.[Number(this.dataset.optionIndex)];
      const colour = this.listButtons.find((item) => item.dataset.value === value)?.dataset.colour;
      const held = this.held;
      this.held = null;
      if (!colour) return;
      this.show(colour);
      if (held?.colour === colour) this.keepPlace(held);
    }

    /**
     * After a colour picked here, Dawn focuses the main picker's input (which scrolls the page up to it) and then
     * scrolls up to the new featured photo. Keep the showcase where it was on screen and give focus back.
     * The microtask runs right after Dawn's focus() call; the timer runs after Dawn's photo-scroll timer.
     */
    keepPlace({ focus, top }) {
      const hold = () => {
        window.scrollTo(window.scrollX, window.scrollY + this.getBoundingClientRect().top - top);
      };
      queueMicrotask(() => {
        if (focus && focus !== document.body && focus.isConnected) focus.focus({ preventScroll: true });
        else document.activeElement?.blur?.();
        hold();
      });
      setTimeout(hold);
    }

    show(colour) {
      if (!this.colours.includes(colour)) return;
      if (colour !== this.selected) this.settledAt = performance.now() + PulseColourShowcase.SETTLE_MS;
      this.selected = colour;
      this.dataset.selected = colour;

      this.querySelectorAll('.pulse-showcase__img').forEach((image) => {
        const active = image.dataset.colour === colour;
        if (active) image.loading = 'eager';
        image.classList.toggle('is-active', active);
      });

      const offered = new Set(this.listButtons.map((item) => item.dataset.colour));
      const order = PulseColourShowcase.slotOrder(colour, this.colours);
      this.querySelectorAll('.pulse-showcase__slot').forEach((slot) => {
        const slotColour = order[Number(slot.dataset.slot)];
        slot.dataset.colour = slotColour;
        slot.hidden = !offered.has(slotColour);
      });

      this.listButtons.forEach((item) => item.setAttribute('aria-pressed', String(item.dataset.colour === colour)));
    }
  }

  customElements.define('pulse-colour-showcase', PulseColourShowcase);
}
